import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaSave, FaTrash } from 'react-icons/fa';
import {
  createSession, getSession, patchSession, deleteSession
} from '../../../services/onboardingWizardService';
import Step1UploadPlan from './steps/Step1UploadPlan';
import Step2DropPins from './steps/Step2DropPins';
import Step3Categorise from './steps/Step3Categorise';
import Step4ReviewIds from './steps/Step4ReviewIds';
import Step5Confirm from './steps/Step5Confirm';
import './OnboardingWizard.css';

const STEPS = [
  { n: 1, label: 'Upload plan', short: 'Plan' },
  { n: 2, label: 'Drop pins', short: 'Pins' },
  { n: 3, label: 'Categorise', short: 'Categories' },
  { n: 4, label: 'Review IDs', short: 'IDs' },
  { n: 5, label: 'Confirm', short: 'Confirm' },
];

function OnboardingWizard() {
  const { id: propertyId, sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isStaffMode = location.pathname.startsWith('/cloud-staff/');
  const staffBase = `/cloud-staff/asset-generation-v2/${propertyId}`;
  const propertyBase = `/propertydashboard/${propertyId}/onboard`;

  const [session, setSession] = useState(null);
  const [pins, setPins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);

  const autosaveTimer = useRef();
  const pendingPatch = useRef({});

  const applyBundle = (b) => {
    if (!b) return;
    setSession(b.session);
    setPins(b.pins || []);
    setCategories(b.categories || []);
  };

  // On mount: load existing session or create a new draft.
  useEffect(() => {
    (async () => {
      try {
        if (urlSessionId) {
          const bundle = await getSession(urlSessionId);
          applyBundle(bundle);
        } else {
          // Default module = roof_safety unless caller specifies (?module=fire_safety etc.)
          const url = new URL(window.location.href);
          const moduleKey = url.searchParams.get('module') || 'roof_safety';
          const bundle = await createSession(propertyId, { module_key: moduleKey });
          applyBundle(bundle);
          navigate(`${isStaffMode ? staffBase : propertyBase}/${bundle.session.id}`, { replace: true });
        }
      } catch (e) {
        toast.error(e.message || 'Failed to load onboarding session');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced autosave of session fields
  const queuePatch = (patch) => {
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(flushPatch, 600);
  };
  const flushPatch = async () => {
    const p = pendingPatch.current;
    pendingPatch.current = {};
    if (!session || !Object.keys(p).length) return;
    try {
      setSaving(true);
      const bundle = await patchSession(session.id, p);
      applyBundle(bundle);
      setAutoSavedAt(new Date());
    } catch (e) {
      toast.error(e.message || 'Auto-save failed');
    } finally {
      setSaving(false);
    }
  };

  // Periodic auto-save heartbeat every 30s
  useEffect(() => {
    const h = setInterval(() => { flushPatch(); }, 30000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const goToStep = (n) => {
    if (!session) return;
    if (n > session.current_step) return; // can't jump forward past incomplete steps
    queuePatch({ current_step: n });
  };

  const advance = (toStep) => {
    if (!session) return;
    queuePatch({ current_step: Math.max(session.current_step, toStep) });
  };

  const handleDelete = async () => {
    if (!session || session.status !== 'draft') return;
    if (!window.confirm('Delete this draft onboarding session? This cannot be undone.')) return;
    try {
      await deleteSession(session.id);
      toast.success('Draft deleted');
      navigate(isStaffMode ? '/cloud-staff/asset-generation-v2' : `/propertydashboard/${propertyId}`);
    } catch (e) {
      toast.error(e.message || 'Failed to delete draft');
    }
  };

  const handleSaveDraft = async () => {
    await flushPatch();
    toast.success('Draft saved');
  };

  if (loading) return <div className="ow-loading">Loading onboarding session…</div>;
  if (!session) return <div className="ow-loading">Session unavailable.</div>;

  const step = session.current_step || 1;

  const stepProps = {
    session, pins, categories, propertyId,
    onApplyBundle: applyBundle,
    onAdvance: advance,
    onQueuePatch: queuePatch,
    onFlush: flushPatch,
  };

  return (
    <div className="ow-page">
      <header className="ow-header">
        <button className="ow-back" onClick={() => navigate(isStaffMode ? '/cloud-staff/asset-generation-v2' : `/propertydashboard/${propertyId}`)}>
          <FaArrowLeft /> Exit
        </button>
        <div className="ow-title-block">
          <div className="ow-kicker">{moduleLabel(session.module_key)} · Onboard assets</div>
          <h1 className="ow-title">{session.title || 'New onboarding session'}</h1>
          <div className="ow-meta">
            <span className={`ow-status ow-status-${session.status}`}>{session.status}</span>
            <span className="ow-save-state">
              {saving ? 'Saving…' : autoSavedAt ? `Auto-saved ${timeAgo(autoSavedAt)}` : 'All changes saved'}
            </span>
          </div>
        </div>
        <div className="ow-header-actions">
          <button className="ow-btn ow-btn-ghost" onClick={handleSaveDraft}><FaSave /> Save draft</button>
          <button className="ow-btn ow-btn-muted" onClick={handleDelete}><FaTrash /> Delete draft</button>
        </div>
      </header>

      <ol className="ow-steps" aria-label="Wizard progress">
        {STEPS.map((s, idx) => {
          const done = step > s.n;
          const active = step === s.n;
          const reachable = step >= s.n;
          return (
            <li key={s.n} className={`ow-step ${active ? 'is-active' : ''} ${done ? 'is-done' : ''} ${reachable ? '' : 'is-locked'}`}>
              <button
                className="ow-step-btn"
                disabled={!reachable || active}
                onClick={() => goToStep(s.n)}
                aria-current={active ? 'step' : undefined}
              >
                <span className="ow-step-num">{done ? '✓' : s.n}</span>
                <span className="ow-step-label">
                  <em>Step {s.n}</em>
                  <strong>{s.label}</strong>
                </span>
              </button>
              {idx < STEPS.length - 1 && <span className="ow-step-rail" aria-hidden />}
            </li>
          );
        })}
      </ol>

      <main className="ow-main">
        {step === 1 && <Step1UploadPlan {...stepProps} />}
        {step === 2 && <Step2DropPins {...stepProps} />}
        {step === 3 && <Step3Categorise {...stepProps} />}
        {step === 4 && <Step4ReviewIds {...stepProps} />}
        {step === 5 && <Step5Confirm {...stepProps} />}
      </main>
    </div>
  );
}

function moduleLabel(key) {
  return ({ roof_safety: 'Roof Safety', fire_safety: 'Fire Safety', equipment_safety: 'Equipment Safety' })[key] || key;
}
function timeAgo(d) {
  const s = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return d.toLocaleTimeString();
}

export default OnboardingWizard;
